import { useCallback, useEffect, useState } from 'react';
import axios from '../config/axios';
import { getApiUrl } from '../config/environment';
import { isLocalPrescriptionModel } from '../utils/prescriptionModelApply';

export async function fetchPrescriptionForModel(prescription: any): Promise<any> {
  if (!prescription?.id) return prescription;
  const hasStructuredItems = Array.isArray(prescription.items) && prescription.items.length > 0;
  const hasFreeText = prescription.format === 'free_text' && String(prescription.body_text || '').trim();
  if (hasStructuredItems || hasFreeText) return prescription;

  try {
    const res = await axios.get(getApiUrl(`/clinic/prescriptions/${prescription.id}`));
    return res.data?.data ?? res.data ?? prescription;
  } catch {
    return prescription;
  }
}

export function usePatientPrescriptionHistory(
  patientId: string | null | undefined,
  options?: {
    enabled?: boolean;
    limit?: number;
    excludePrescriptionId?: string | null;
  },
) {
  const enabled = options?.enabled !== false;
  const limit = options?.limit ?? 40;
  const excludePrescriptionId = options?.excludePrescriptionId || null;

  const [history, setHistory] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);

  const loadHistory = useCallback(async () => {
    if (!patientId || !enabled) {
      setHistory([]);
      return [];
    }

    setLoading(true);
    try {
      const res = await axios.get(getApiUrl('/clinic/prescriptions'), {
        params: { patient_id: patientId, limit },
      });
      const rows = (res.data?.data || [])
        .filter(isLocalPrescriptionModel)
        .filter((row: any) => !excludePrescriptionId || String(row.id) !== String(excludePrescriptionId));
      setHistory(rows);
      return rows;
    } catch {
      setHistory([]);
      return [];
    } finally {
      setLoading(false);
    }
  }, [enabled, excludePrescriptionId, limit, patientId]);

  useEffect(() => {
    void loadHistory();
  }, [loadHistory]);

  return { history, loading, reload: loadHistory };
}
